import { useEffect, useState } from 'react';
import IframeResizer from 'iframe-resizer-react';
import { debounce } from 'debounce';
import styled from 'styled-components';
import Loader from '../../../components/loader';
import LogRocket from 'logrocket';

type BankStatementProps = {
  url: string;
  customerNumber: string;
  loading: boolean;
  onBsoSubmitted: () => void;
  onBsoModified: () => void;
};

enum IframePostMessageEvents {
  BANK_SELECTED = 'bank_selected',
  LOGIN_CLICKED = 'login_clicked',
  ACCOUNTS_SELECTED = 'accounts_selected',
  SUBMISSION_COMPLETE = 'submission_complete',
  SUBMISSION_ALL = 'submit_all',
}

const SCBankStatementDetails = styled.div<{
  iframeHeight: number;
  loading: boolean;
  iframeResizing: boolean;
}>`
  .loading-overlay {
    display: ${(props) => (props.iframeResizing || props.loading ? 'block' : 'none')};
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    margin: auto;
  }

  iframe {
    opacity: ${(props) => (props.iframeResizing || props.loading ? 0 : 1)};
    width: 100%;
    border: 0px;
  }

  .bso-iframe {
    max-height: ${(props) => props.iframeHeight}px;
    transition: max-height 0.15s ease-out;
    overflow: hidden;
  }

  .bso-iframe-wrapper {
    position: relative;
    min-height: 300px;
  }
`;

export const IFrameBankStatements = ({
  url,
  customerNumber,
  loading,
  onBsoSubmitted,
  onBsoModified,
}: BankStatementProps) => {
  const source_url = new URL(url);
  const customerBsoUrl = source_url.href + '-' + customerNumber;

  const iframeMinHeight = 380;
  const iframeMaxHeight = loading ? iframeMinHeight : 630;
  const [iframeResizing, setIframeResizing] = useState<boolean>(true);
  const [iframeHeight, setIframeHeight] = useState<number>(iframeMinHeight);

  const debouncedShowIframe = debounce(() => {
    setIframeResizing(false);
  }, 3000);

  const handleMessages = (event: any) => {
    if (event.origin !== source_url.origin) {
      return false;
    }

    if (event.data.includes('[iFrameSizer]')) {
      debouncedShowIframe();
      return;
    }

    onBsoModified();

    const eventName = JSON.parse(event.data).event;

    LogRocket.track('BSO event', {
      message: `event received: ${eventName}`,
    });

    if (eventName === IframePostMessageEvents.SUBMISSION_ALL) {
      onBsoSubmitted();
      return;
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessages, false);

    return () => window.removeEventListener('message', handleMessages, false);
  }, []);

  return (
    <SCBankStatementDetails
      iframeHeight={iframeHeight}
      loading={loading}
      iframeResizing={iframeResizing}
      data-chromatic="ignore"
      data-private
    >
      <div className="bso-iframe">
        <div className="bso-iframe-wrapper">
          <div className="loading-overlay">
            <Loader text="Validating bank statement data.." />
          </div>
          <IframeResizer
            src={customerBsoUrl}
            id="bso_iframe"
            title="Bank Statements Online"
            checkOrigin={[source_url.origin]}
            heightCalculationMethod="bodyOffset"
            tolerance={4}
            onResized={(e) => setIframeHeight(Math.max(e.height, iframeMaxHeight))}
          />
        </div>
      </div>
    </SCBankStatementDetails>
  );
};
